import React, { FC, useEffect, useMemo, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import Box from '@mui/material/Box'
import Link from '@mui/material/Link'
import Typography from '@mui/material/Typography'
import Loading from '../components/system/Loading'
import ErrorComponent from '../components/system/Error'
import VerifiedPlayer from '../verification/VerifiedPlayer'
import { resolvePublicContainer } from '../system/apitools'
import { latestVersionIndex, requestedVersionIndex, deletedVideoIds } from './versionContext'
import useRouter from '../hooks/useRouter'

const PUBLIC_API = '/api/v1/public/videos'

// Optional ?radar=<filename> query param picks the compass background image
// from /img/ (e.g. ?radar=radar-v2.png). Restricted to a bare filename so the
// value can't point outside that folder.
const radarImageFromQuery = (): string | undefined => {
  const radar = new URLSearchParams(window.location.search).get('radar')
  if (!radar || !/^[\w-]+\.\w+$/.test(radar)) return undefined
  return `/img/${radar}`
}

// The public page resolves a CONTAINER KEY, not a video id: `/video/:key`
// accepts a slug, a slug this container used to answer to, the container id,
// or any version's video id, and the API says which of those it was
// (`resolved_kind`) plus the one canonical slug all of them should settle on.
// Anything that isn't already the canonical slug redirects to it, so a link
// pasted anywhere ends up at the address the publisher chose — the in-app
// half of the redirect rule, since the CDN layer is explicitly out of scope.
//
// `/video/:key/v/:index` renders one specific version. It is a deliberate
// link to a superseded publication, not an accident, so the page says so
// plainly and plays it anyway: those bytes still hash to the root their
// publisher sealed, and verification treats them exactly as it treats the
// current version's.
const PublicVideoPage: FC = () => {
  const { params, navigate } = useRouter()
  const key = params.key
  const versionIndex = requestedVersionIndex(params.index)
  const radarImage = useMemo(radarImageFromQuery, [])

  const malformedVersion = params.index !== undefined && versionIndex === undefined

  const { isPending, error, data: resolution } = useQuery({
    queryKey: ['public-container', key, versionIndex],
    queryFn: () => resolvePublicContainer(key, versionIndex),
    enabled: !!key && !malformedVersion,
    // A mistyped slug is a 404, and a 404 does not become a 200 by asking
    // again. React Query's default is three retries with backoff, which left
    // a stranger looking at a spinner for about fifteen seconds before being
    // told the link was wrong. Server errors are still worth retrying.
    retry: (failureCount, error) => {
      const status = (error as { response?: { status?: number } })?.response?.status
      if (status !== undefined && status >= 400 && status < 500) return false
      return failureCount < 3
    },
  })

  // The canonical redirect. In an effect rather than during render because
  // navigating mid-render is a side effect on the router, and because the
  // query has to have come back before there is a canonical slug to go to.
  const resolvedKind = resolution?.resolved_kind
  const canonicalSlug = resolution?.canonical_slug
  //
  // The rest of the query string rides along (the router keeps unknown query
  // parameters in `params`). Dropping it made every player flag — ?debug,
  // ?split, ?probe — vanish from any link that used an id instead of the
  // slug, before the player had mounted to read it.
  const paramsRef = useRef(params)
  paramsRef.current = params
  useEffect(() => {
    if (!canonicalSlug || resolvedKind === 'slug') return
    const { index: _index, ...rest } = paramsRef.current
    if (versionIndex === undefined) navigate('video', { ...rest, key: canonicalSlug })
    else navigate('video-version', { ...rest, key: canonicalSlug, index: versionIndex })
  }, [canonicalSlug, resolvedKind, versionIndex, navigate])

  const video = resolution?.video
  const container = resolution?.container

  const { participants, attributions, subtitleOffset } = useMemo(() => {
    if (!video) return { participants: [], attributions: {}, subtitleOffset: 0 }
    return {
      participants: video.participants || [],
      attributions: video.speaker_attributions || {},
      subtitleOffset: video.subtitle_offset || 0,
    }
  }, [video])

  const participantsWithAvatarUrls = useMemo(() => {
    if (!video) return []
    return participants.map((p) => ({
      ...p,
      avatar_url: p.avatar_url
        // Keyed on the VERSION's video id, not the container key: every asset
        // route is still per-video and every version keeps its own copies.
        ? `${PUBLIC_API}/${video.id}/participants/${p.id}/avatar/image?v=${encodeURIComponent(p.avatar_url)}`
        : undefined,
    }))
  }, [participants, video])

  if (!key) return <ErrorComponent message="Video not found" />
  if (malformedVersion) return <ErrorComponent message="Version not found" />
  if (isPending) return <Loading />
  if (error) return <ErrorComponent message={error.message} />
  if (!video || !container) return <ErrorComponent message="Video not found" />

  if (video.state !== 'ready' || !video.stream_url) {
    return <ErrorComponent message="Video not available" />
  }

  const subtitleContentUrl = video.subtitles
    ? `${PUBLIC_API}/${video.id}/subtitles/content`
    : undefined

  // The off-chain half of the version story: this page was asked for a
  // specific version and the container's own list says a later one exists.
  // Independent of the chain read inside the player — that one is what
  // catches an API serving a stale version WITHOUT being asked to, and this
  // one is what explains a link the viewer followed on purpose.
  const latestIndex = latestVersionIndex(container)
  const supersededByRequest = versionIndex !== undefined && latestIndex !== null && versionIndex !== latestIndex
  const currentHref = canonicalSlug ? `/video/${canonicalSlug}` : undefined

  // T19: the same player this page has always rendered, handed to
  // VerifiedPlayer so it can add what verification owns — the merged subtitle
  // picker, the segment verifier, the terminal failure and the badge.
  // `subtitleLanguages` is deliberately absent: the sealed base's languages
  // come off the video there, merged with any addendum the publisher anchored
  // after sealing, minus any whose bytes failed their own on-chain hash.
  return (
    <Box sx={{ height: '100dvh', overflow: 'hidden', backgroundColor: '#000', display: 'flex', flexDirection: 'column' }}>
      {supersededByRequest && (
        <Box
          sx={{
            flexShrink: 0,
            px: 2,
            py: 1,
            backgroundColor: 'rgba(255,167,38,0.18)',
            borderBottom: '1px solid rgba(255,167,38,0.5)',
            color: '#ffcc80',
          }}
        >
          <Typography variant="body2" component="span">
            You’re watching version {versionIndex + 1} of this video. A newer version has since been published.{' '}
          </Typography>
          {currentHref && (
            <Link href={currentHref} sx={{ color: '#ffcc80', fontWeight: 600 }}>
              Watch the current version
            </Link>
          )}
        </Box>
      )}
      <Box sx={{ flexGrow: 1, minHeight: 0, overflow: 'hidden' }}>
        <VerifiedPlayer
          video={video}
          versions={{
            containerId: container.id,
            containerAuthority: container.chain?.authority,
            deletedVideoIds: deletedVideoIds(container),
            versionList: container.versions,
            currentHref,
          }}
          player={{
            src: video.stream_url,
            type: 'application/x-mpegURL',
            // The programme title, top-left in the player. `name` is only the
            // fallback line, for a video saved before series and episode
            // existed; with all three empty the overlay renders nothing and
            // the player looks exactly as it did.
            series: video.series,
            episode: video.episode,
            name: video.name,
            participants: participantsWithAvatarUrls,
            subtitleUrl: subtitleContentUrl,
            subtitleOffset,
            speakerAttributions: attributions,
            rotationAdjustment: video.rotation_adjustment,
            fourView: video.four_view,
            startTilt: video.start_tilt,
            fillHeight: true,
            autoPlay: true,
            masterLanguage: video.subtitles?.master_language,
            radarImage,
          }}
        />
      </Box>
    </Box>
  )
}

export default PublicVideoPage
